/**
 * Per-widget config schemas.
 *
 * These live in core (not ui) so they stay platform-free and unit-testable.
 * `packages/ui/registry.ts` pairs each one with the component that draws it.
 */

import { z } from 'zod';
import { WidgetBaseSchema, describeTimeZoneProblem } from './schema.js';
import type { Predicate, Query } from './query.js';

// ─── clock ──────────────────────────────────────────────────────────────────

/**
 * A zone is either `"America/New_York"` or `{ label: EST, tz: America/New_York }`.
 *
 * Region zones are required, not abbreviations. `America/New_York` is EST in
 * January and EDT in July and the platform handles that switch; `EST` is a
 * real tz-database entry that `Intl` accepts but is pinned to UTC-5 forever.
 * `describeTimeZoneProblem` rejects that case with the fix spelled out — a
 * label of "EST" on a `America/New_York` zone is the correct way to get that
 * text on screen.
 */
const ZoneSchema = z
  .union([
    z.string().min(1),
    z.object({ label: z.string().min(1).optional(), tz: z.string().min(1) }),
  ])
  .transform((value) => (typeof value === 'string' ? { tz: value } : value))
  .superRefine((value, ctx) => {
    const problem = describeTimeZoneProblem(value.tz);
    if (problem) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem });
    }
  });

export const ClockConfigSchema = WidgetBaseSchema.extend({
  type: z.literal('clock'),
  zones: z.array(ZoneSchema).min(1).max(6),
  seconds: z.boolean().default(true),
  hour12: z.boolean().default(true),
  showDate: z.boolean().default(true),
  /** Which zone's date to show. Defaults to the first. */
  dateZone: z.string().optional(),
});

export type ClockConfig = z.infer<typeof ClockConfigSchema>;
export type ClockZone = ClockConfig['zones'][number];

// ─── collection ─────────────────────────────────────────────────────────────

const PredicateOpsSchema = z
  .object({
    eq: z.unknown().optional(),
    ne: z.unknown().optional(),
    gt: z.unknown().optional(),
    gte: z.unknown().optional(),
    lt: z.unknown().optional(),
    lte: z.unknown().optional(),
    in: z.array(z.unknown()).optional(),
    nin: z.array(z.unknown()).optional(),
    exists: z.boolean().optional(),
    contains: z.string().optional(),
    matches: z.string().optional(),
  })
  .strict();

/** `status: reading` for the common case, `rating: { gte: 4 }` when you need an operator. */
const PredicateSchema: z.ZodType<Predicate> = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
  PredicateOpsSchema,
]) as z.ZodType<Predicate>;

const SortSpecSchema = z.union([
  z.string().min(1),
  z.object({ by: z.string().min(1), dir: z.enum(['asc', 'desc']).default('asc') }),
]);

export const QuerySchema: z.ZodType<Query, z.ZodTypeDef, unknown> = z.object({
  from: z.string().min(1),
  where: z.record(PredicateSchema).optional(),
  sort: z.union([SortSpecSchema, z.array(SortSpecSchema)]).optional(),
  limit: z.number().int().positive().optional(),
}) as z.ZodType<Query, z.ZodTypeDef, unknown>;

/**
 * How one query result is drawn. Every value here is the NAME of a frontmatter
 * field, not the value itself — `title: name` means "read the `name` field".
 */
const ItemSchema = z
  .object({
    layout: z.enum(['row', 'progress', 'card']).default('row'),
    /** Falls back to the filename when the field is missing. */
    title: z.string().default('title'),
    subtitle: z.string().optional(),
    /** Right-aligned muted metadata, matching the reference dashboard's list rows. */
    meta: z.string().optional(),
    badge: z.string().optional(),
    /** `progress` layout only. */
    value: z.string().default('current'),
    target: z.string().default('target'),
    unit: z.string().optional(),
  })
  .default({});

/**
 * The extensibility widget.
 *
 * Query the vault by frontmatter and render the results. Tracking something new
 * — books, films, workouts, people — is a folder of markdown files plus one of
 * these configs. No new widget type, no code, no release.
 */
export const CollectionConfigSchema = WidgetBaseSchema.extend({
  type: z.literal('collection'),
  from: z.string().min(1),
  where: z.record(PredicateSchema).optional(),
  sort: z.union([SortSpecSchema, z.array(SortSpecSchema)]).optional(),
  limit: z.number().int().positive().optional(),
  item: ItemSchema,
  /** Shown when the query matches nothing — worth setting, since an empty tile reads as broken. */
  empty: z.string().default('Nothing here'),
  /** Show the match count next to the header. */
  showCount: z.boolean().default(false),
});

export type CollectionConfig = z.infer<typeof CollectionConfigSchema>;
export type CollectionItemConfig = CollectionConfig['item'];

// ─── tasklist ───────────────────────────────────────────────────────────────

export const TaskListConfigSchema = WidgetBaseSchema.extend({
  type: z.literal('tasklist'),
  /**
   * Vault path. `{today}` expands to the current date in `timezone`, so
   * `todos/{today}.md` follows you through the week without any edits.
   */
  file: z.string().min(1),
  /** Override the vault's zone for this widget only. Rarely what you want. */
  timezone: z.string().optional(),
  hideCompleted: z.boolean().default(false),
  /** Completed tasks sink to the bottom rather than holding their file position. */
  completedLast: z.boolean().default(true),
  limit: z.number().int().positive().optional(),
  empty: z.string().default('Nothing left'),
});

export type TaskListConfig = z.infer<typeof TaskListConfigSchema>;

// ─── banner ─────────────────────────────────────────────────────────────────

export const BannerConfigSchema = WidgetBaseSchema.extend({
  type: z.literal('banner'),
  /** Vault-relative path or an absolute URL. */
  src: z.string().min(1),
  alt: z.string().default(''),
  fit: z.enum(['cover', 'contain']).default('cover'),
  /** CSS `object-position`, for pushing the focal point of a wide crop. */
  position: z.string().default('center'),
  caption: z.string().optional(),
});

export type BannerConfig = z.infer<typeof BannerConfigSchema>;

// ─── weekstrip ──────────────────────────────────────────────────────────────

export const WeekStripConfigSchema = WidgetBaseSchema.extend({
  type: z.literal('weekstrip'),
  /** Override the vault's zone for this widget only. Rarely what you want. */
  timezone: z.string().optional(),
  startOn: z.enum(['monday', 'sunday']).default('monday'),
  /** Link each day to a dashboard or note, with `{date}` expanded. */
  linkTo: z.string().optional(),
});

export type WeekStripConfig = z.infer<typeof WeekStripConfigSchema>;

// ─── nav ────────────────────────────────────────────────────────────────────

const NavLinkSchema = z.object({
  label: z.string().min(1),
  /** A dashboard name (`tasks`) or an absolute URL. */
  to: z.string().min(1),
});

export const NavConfigSchema = WidgetBaseSchema.extend({
  type: z.literal('nav'),
  links: z.array(NavLinkSchema).min(1),
  direction: z.enum(['vertical', 'horizontal']).default('vertical'),
});

export type NavConfig = z.infer<typeof NavConfigSchema>;
export type NavLink = z.infer<typeof NavLinkSchema>;

// ─── keyvalue ───────────────────────────────────────────────────────────────

const KeyValueRowSchema = z.object({
  label: z.string().min(1),
  /** A literal value, or `count:<glob>` to count matching files. */
  value: z.union([z.string(), z.number()]),
});

export const KeyValueConfigSchema = WidgetBaseSchema.extend({
  type: z.literal('keyvalue'),
  rows: z.array(KeyValueRowSchema).min(1),
});

export type KeyValueConfig = z.infer<typeof KeyValueConfigSchema>;
export type KeyValueRow = z.infer<typeof KeyValueRowSchema>;

// ─── weather ────────────────────────────────────────────────────────────────

export const WeatherConfigSchema = WidgetBaseSchema.extend({
  type: z.literal('weather'),
  /** Source id — reads `data/<source>/latest.json`. */
  source: z.string().min(1),
  showHighLow: z.boolean().default(true),
  /**
   * Flag the reading as stale past this many minutes. Should exceed the
   * schedule's interval, or the tile cries wolf between every run.
   */
  staleAfterMinutes: z.number().int().positive().default(90),
});

export type WeatherConfig = z.infer<typeof WeatherConfigSchema>;

// ─── quotes ─────────────────────────────────────────────────────────────────

export const QuotesConfigSchema = WidgetBaseSchema.extend({
  type: z.literal('quotes'),
  /** Source id — reads `data/<source>/latest.json`. */
  source: z.string().min(1),
  /** Hide the percent-change column for a plain price list. */
  showChange: z.boolean().default(true),
  staleAfterMinutes: z.number().int().positive().default(90),
});

export type QuotesConfig = z.infer<typeof QuotesConfigSchema>;

// ─── Registry ───────────────────────────────────────────────────────────────

/**
 * Every widget type and its config schema, in one place.
 *
 * This is the single list. `packages/ui` registers components against these
 * schemas, and `scripts/gen-agents.ts` generates the vault's agent
 * documentation from them — so the docs an AI reads cannot drift from what the
 * validator actually enforces. A test asserts the UI registry matches this map.
 */
export const WIDGET_SCHEMAS = {
  banner: BannerConfigSchema,
  clock: ClockConfigSchema,
  collection: CollectionConfigSchema,
  keyvalue: KeyValueConfigSchema,
  nav: NavConfigSchema,
  quotes: QuotesConfigSchema,
  tasklist: TaskListConfigSchema,
  weather: WeatherConfigSchema,
  weekstrip: WeekStripConfigSchema,
} as const;

export type WidgetType = keyof typeof WIDGET_SCHEMAS;

/** Short zone abbreviation for the current instant — "EST" in winter, "EDT" in summer. */
export function zoneAbbreviation(tz: string, at: Date): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    timeZoneName: 'short',
  }).formatToParts(at);
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? tz;
}

export function zoneLabel(zone: ClockZone, at: Date): string {
  return zone.label ?? zoneAbbreviation(zone.tz, at);
}
